// Pure geometry for locked columns and the proxy scrollbar. The region clips horizontally rather than
// scrolling, so the page stays the only scroll container and the native sticky header keeps pinning; the
// proxy's scrollLeft translates the table, and locked cells translate back by the same amount.

import { RING_SPACE } from './controlSize'

/** Control cells that lock with the table's start and end edges, by class. A data column of the actions cell
 * type shares .me-table-actions and carries data-column-key; it locks only as a configured lock. */
export const START_CONTROL = '.me-table-control, .me-table-select'
export const END_CONTROL = '.me-table-actions:not([data-column-key])'

/** Space a focused control keeps clear of a locked edge, for its focus ring (one home: controlSize.js). */
export { RING_SPACE }

/** The CSS variables the region carries; each locked cell translates by its edge's variable. */
export const VARS = {
  scroll: '--me-table-scroll-x',
  start: '--me-table-lock-start-x',
  end: '--me-table-lock-end-x',
  width: '--me-table-width',
}

/**
 * Horizontal overflow of the table inside its region.
 * @param {number} tableWidth the table's layout width
 * @param {number} visibleWidth the region's client width
 * @returns {number} the furthest the table can scroll, 0 when it fits
 */
export const maxScroll = (tableWidth, visibleWidth) =>
  tableWidth > 0 && visibleWidth > 0
    ? Math.max(0, Math.round(tableWidth - visibleWidth))
    : 0

/** A scroll position kept inside 0..max. */
export const clampScroll = (left, max) =>
  Math.min(Math.max(0, Math.round(left) || 0), Math.max(0, max))

/**
 * What the region paints at a scroll position: the variables, and which edge shadows show. A shadow shows
 * only while content is scrolled under that edge.
 * @param {number} left
 * @param {number} max
 */
export const scrollState = (left, max) => {
  const x = clampScroll(left, max)
  return {
    vars: {
      [VARS.scroll]: `${-x}px`,
      [VARS.start]: `${x}px`,
      [VARS.end]: `${x - max}px`,
    },
    shadowStart: x > 0,
    shadowEnd: x < max,
  }
}

const NONE = new Set()

const sideOf = column =>
  column.lock === 'start' || column.lock === 'end' ? column.lock : null

/**
 * The configured locks the table lets go of because they do not fit: while the locked width (every
 * locked column plus every control column, which always keeps its lock) leaves the scrolling columns less
 * than half the visible width, end locks release first, innermost outward, then start locks, innermost
 * first. Render-time only: the View and the config keep every lock, and a released lock returns once its
 * width fits again.
 * @param {Array<{key: string, lock: ?string}>} columns display order
 * @param {Array<{key: ?string, width: number}>} widths the rendered columns in header order; key is null
 *   for a control column
 * @param {number} visible the region's visible width; nothing releases before it is measured
 * @returns {Set<string>} the keys of the released columns
 */
export const releasedLocks = (columns, widths, visible) => {
  if (!(visible > 0) || !Array.isArray(widths) || !widths.length) return NONE
  const widthOf = new Map()
  let locked = 0
  widths.forEach(col => {
    const width = Number(col.width) || 0
    if (col.key === null) locked += width
    else widthOf.set(col.key, width)
  })
  const starts = []
  const ends = []
  columns.forEach(column => {
    const side = sideOf(column)
    if (side === 'start') starts.push(column.key)
    else if (side === 'end') ends.push(column.key)
  })
  starts.concat(ends).forEach(key => {
    locked += widthOf.get(key) || 0
  })
  const released = new Set()
  // innermost end lock first (the first end-locked column in display order), then the innermost start lock
  const order = ends.concat(starts.slice().reverse())
  for (let i = 0; i < order.length && visible - locked < visible / 2; i += 1) {
    released.add(order[i])
    locked -= widthOf.get(order[i]) || 0
  }
  return released
}

/**
 * The locked edge of every configured column, by key: start-locked columns sit first in display order and
 * end-locked last (view/view.js), so the innermost one on each side carries that side's edge shadow. A
 * released column (releasedLocks) scrolls, and the next lock outward carries its side's edge.
 * @param {Array<{key: string, lock: ?string}>} columns display order
 * @param {Set<string>} [released] keys whose lock is released this render
 */
export const lockPlan = (columns, released = NONE) => {
  const sides = new Map()
  let startEdge = null
  let endEdge = null
  columns.forEach(column => {
    const side = released.has(column.key) ? null : sideOf(column)
    sides.set(column.key, side)
    if (side === 'start') startEdge = column.key
    if (side === 'end' && endEdge === null) endEdge = column.key
  })
  return { sides, startEdge, endEdge }
}

/** The inline style that places a locked cell, or undefined for one that scrolls. */
export const lockStyleFor = side =>
  side ? { transform: `translateX(var(${VARS[side]}, 0px))` } : undefined

/**
 * The scroll position that brings a focused control clear of both locked edges, keeping its ring space; the
 * current position when it is already clear. Coordinates are viewport pixels as rendered.
 * @param {object} at
 * @param {number} at.left current scroll position
 * @param {number} at.max
 * @param {number} at.clearLeft where the start-locked cells end
 * @param {number} at.clearRight where the end-locked cells begin
 * @param {number} at.elementLeft
 * @param {number} at.elementRight
 */
export const revealScroll = ({
  left,
  max,
  clearLeft,
  clearRight,
  elementLeft,
  elementRight,
}) => {
  const from = clearLeft + RING_SPACE
  const to = clearRight - RING_SPACE
  // the table moves in whole pixels, so each move rounds the way that keeps the ring space: further back
  // when revealing past the start edge, further on past the end edge, and never past the start edge's space
  let next = left
  if (elementLeft < from) next = left - Math.ceil(from - elementLeft)
  else if (elementRight > to)
    next =
      left +
      Math.min(Math.ceil(elementRight - to), Math.floor(elementLeft - from))
  return clampScroll(next, max)
}

/** Pointer travel from a middle-button press that scrolls nothing, per axis. */
export const AUTOSCROLL_DEAD_ZONE = 12

/** Scroll speed per pixel of travel past the dead zone, in pixels per second. */
export const AUTOSCROLL_GAIN = 8

/**
 * The speed a middle-button autoscroll moves along one axis, as browser autoscroll does on a native scroll
 * region: nothing inside the dead zone, then faster the further the pointer travels from the press point.
 * @param {number} offset pointer position less the press point, on that axis
 * @returns {number} pixels per second, signed like the offset
 */
export const autoscrollSpeed = offset => {
  const past = Math.abs(offset) - AUTOSCROLL_DEAD_ZONE
  if (!(past > 0)) return 0
  return Math.sign(offset) * past * AUTOSCROLL_GAIN
}

/**
 * A horizontal wheel or trackpad movement, or a vertical one with Shift held, as a horizontal delta; 0 when
 * the gesture is vertical.
 * @param {{deltaX: number, deltaY: number, shiftKey: boolean, deltaMode?: number}} event
 */
export const wheelDelta = event => {
  const scale = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? 400 : 1
  if (event.shiftKey && !event.deltaX) return event.deltaY * scale
  return Math.abs(event.deltaX) > Math.abs(event.deltaY)
    ? event.deltaX * scale
    : 0
}

/** How far a touch travels before its direction decides whether it pans the table. */
export const PAN_SLOP = 8

/**
 * The direction a touch drag commits to: 'horizontal' pans the table, 'vertical' stays the page's scroll,
 * null until it has travelled past the slop.
 * @param {number} dx
 * @param {number} dy
 * @returns {?('horizontal'|'vertical')}
 */
export const panIntent = (dx, dy) => {
  if (Math.max(Math.abs(dx), Math.abs(dy)) < PAN_SLOP) return null
  return Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical'
}
